import { useParams } from "react-router";
import { MODELS, useModel } from "../../api";
import PageShell from "../../design-system/components/PageShell/PageShell";
import Text from "../../design-system/primitives/components/Text/Text";
import { SanityImg } from "../../lib/images.jsx";
import { TagRow } from "./TagRow.jsx";
import BlockContent from "../../design-system/components/BlockContent/BlockContent";
import styles from "./library.css";

/**
 * One library item, filling the shell's sheet. The item's title is the page's
 * title, so it rides in the shell's header rather than being repeated over the
 * body — which leaves the body to the cover, the tags and the text itself.
 *
 * @param {Object} item - The library item to render.
 * @param {string} item.title - The item's title, shown as the page title.
 * @param {string} [item.imageUrl] - Cover image; omitted items simply start at
 *                                   their tags.
 * @param {Object} item.content - The item's content.
 * @param {Object} [item.content.textRaw] - Portable-text body, if it has one.
 *
 * @returns {JSX.Element} The rendered library item page.
 */
const FullLibraryItemWithData = ({ item }) => {
  return (
    <PageShell
      surface="dark"
      titleSlot={<Text variant="h4">{item.title}</Text>}
      titleButton={{
        icon: "arrow-left",
        ariaLabel: "Zurück",
        // Replaces the item in the history instead of stacking the library on
        // top of it, so reading several items in a row doesn't leave a chain
        // of library/item pairs behind the browser's own back button.
        to: "../library",
        replace: true,
      }}
    >
      <article className={styles.fullItem}>
        {item.imageUrl && (
          <SanityImg
            url={item.imageUrl}
            height={650}
            width={1920}
            fit="crop"
            className={styles.fullItemCover}
          />
        )}
        <TagRow item={item} />
        {item.content.textRaw && (
          <BlockContent content={item.content.textRaw} textColor="white" />
        )}
      </article>
    </PageShell>
  );
};

/**
 * FullLibraryItem renders a specific library item based on its route id.
 * Loading suspends and a missing item 404s to the nearest route boundary.
 */
const FullLibraryItem = () => {
  const { libraryItemId } = useParams();

  const item = useModel(MODELS.LIBRARY_ITEM(libraryItemId));

  return <FullLibraryItemWithData item={item} />;
};

export default FullLibraryItem;
